import React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  darkMode: boolean;
  totalItems: number;
  itemsPerPage: number;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  darkMode,
  totalItems,
  itemsPerPage
}) => {
  if (totalPages <= 1) return null;

  const { startItem, endItem, pageNumbers } = React.useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage + 1;
    const end = Math.min(currentPage * itemsPerPage, totalItems);
    
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return { startItem: start, endItem: end, pageNumbers: pages };
  }, [currentPage, totalPages, itemsPerPage, totalItems]);

  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === totalPages;

  const navBtnClass = (disabled: boolean) => `p-2 rounded-lg transition-colors ${
    disabled
      ? darkMode 
        ? 'text-appines-white/25 cursor-not-allowed' 
        : 'text-appines-petrol/25 cursor-not-allowed'
      : darkMode
        ? 'text-appines-white/65 hover:text-appines-white hover:bg-appines-white/10'
        : 'text-appines-petrol/65 hover:text-appines-petrol hover:bg-[rgba(1,42,54,0.06)]'
  }`;

  return (
    <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 mt-8 p-4 rounded-lg ${
      darkMode ? 'bg-appines-petrol' : 'bg-appines-white'
    }`} role="navigation" aria-label="Pagination des résultats">
      <div className={`text-sm ${
        darkMode ? 'text-appines-white/65' : 'text-appines-petrol/65'
      }`} aria-live="polite" aria-atomic="true">
        Affichage de {startItem} à {endItem} sur {totalItems} résultats
      </div>

      <div className="flex items-center space-x-1">
        <button
          onClick={() => onPageChange(1)}
          disabled={isFirstPage}
          className={navBtnClass(isFirstPage)}
          title="Première page"
          aria-label="Aller à la première page"
        >
          <ChevronsLeft className="h-4 w-4" />
        </button>

        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={isFirstPage}
          className={navBtnClass(isFirstPage)}
          title="Page précédente"
          aria-label="Aller à la page précédente"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        <div className="flex items-center space-x-1">
          {pageNumbers.map((page, index) => (
            <React.Fragment key={index}>
              {page === '...' ? (
                <span className={`px-3 py-2 ${
                  darkMode ? 'text-appines-white/35' : 'text-appines-petrol/35'
                }`}>
                  ...
                </span>
              ) : (
                <button
                  onClick={() => onPageChange(page as number)}
                  className={`px-3 py-2 rounded-lg transition-colors ${
                    currentPage === page
                      ? 'bg-appines-petrol text-appines-white'
                      : darkMode
                        ? 'text-appines-white/65 hover:text-appines-white hover:bg-appines-white/10'
                        : 'text-appines-petrol/65 hover:text-appines-petrol hover:bg-[rgba(1,42,54,0.06)]'
                  }`}
                  aria-label={`Aller à la page ${page}`}
                  aria-current={currentPage === page ? 'page' : undefined}
                >
                  {page}
                </button>
              )}
            </React.Fragment>
          ))}
        </div>

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={isLastPage}
          className={navBtnClass(isLastPage)}
          title="Page suivante"
          aria-label="Aller à la page suivante"
        >
          <ChevronRight className="h-4 w-4" />
        </button>

        <button
          onClick={() => onPageChange(totalPages)}
          disabled={isLastPage}
          className={navBtnClass(isLastPage)}
          title="Dernière page"
          aria-label="Aller à la dernière page"
        >
          <ChevronsRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default React.memo(Pagination);
